import { z } from 'zod'

export const relayablyWorkspaceSchemaVersion = '2026-05-19'

export const usageTypeSchema = z.enum(['personal', 'professional', 'business'])
export type UsageType = z.infer<typeof usageTypeSchema>

export const primaryUseCaseSchema = z.enum([
  'screen_personal_calls',
  'capture_sales_enquiries',
  'handle_customer_support',
  'book_appointments',
  'qualify_leads',
  'protect_focus_time',
  'after_hours_answering',
])
export type PrimaryUseCase = z.infer<typeof primaryUseCaseSchema>

export const communicationFormalitySchema = z.enum(['casual', 'balanced', 'formal'])
export const communicationBrevitySchema = z.enum(['concise', 'balanced', 'detailed'])

export const communicationStyleSchema = z.object({
  label: z.string(),
  tone: z.string(),
  formality: communicationFormalitySchema,
  brevity: communicationBrevitySchema,
  notes: z.string().optional().default(''),
})
export type CommunicationStyle = z.infer<typeof communicationStyleSchema>

export const useCaseFlagsSchema = z.object({
  screenPersonalCalls: z.boolean(),
  captureSalesEnquiries: z.boolean(),
  handleCustomerSupport: z.boolean(),
  bookAppointments: z.boolean(),
  qualifyLeads: z.boolean(),
  protectFocusTime: z.boolean(),
  afterHoursAnswering: z.boolean(),
})
export type UseCaseFlags = z.infer<typeof useCaseFlagsSchema>

export const relayablyUserProfileSchema = z.object({
  fullName: z.string(),
  email: z.string().email(),
  country: z.string(),
  timezone: z.string(),
})
export type RelayablyUserProfile = z.infer<typeof relayablyUserProfileSchema>

export const relayablyWorkspaceTemplateSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  config: z.record(z.string(), z.any()).default({}),
})
export type RelayablyWorkspaceTemplate = z.infer<typeof relayablyWorkspaceTemplateSchema>

export const relayablyWorkspaceAgentSchema = z.object({
  id: z.string(),
  role: z.string(),
  goal: z.string(),
  enabled: z.boolean(),
  voiceSelection: z.lazy(() => relayablyVoiceOptionSchema).nullable().default(null),
  config: z.record(z.string(), z.any()).default({}),
})
export type RelayablyWorkspaceAgent = z.infer<typeof relayablyWorkspaceAgentSchema>

export const relayablyPhoneNumberCapabilitySchema = z.enum(['voice', 'sms', 'mms', 'fax'])
export type RelayablyPhoneNumberCapability = z.infer<typeof relayablyPhoneNumberCapabilitySchema>

export const relayablyWorkspacePhoneNumberSchema = z.object({
  id: z.string(),
  provider: z.enum(['twilio', 'mock']).default('twilio'),
  providerResourceId: z.string().optional().default(''),
  phoneNumber: z.string(),
  friendlyName: z.string().optional().default(''),
  country: z.string(),
  region: z.string().optional().default(''),
  locality: z.string().optional().default(''),
  postalCode: z.string().optional().default(''),
  status: z.enum(['available', 'reserved', 'provisioning', 'pending_review', 'active', 'released', 'error']),
  capabilities: z.array(relayablyPhoneNumberCapabilitySchema).default(['voice']),
  monthlyCost: z.string().optional().default(''),
  assignedAt: z.string().optional(),
  forwardingInstructions: z.string().optional().default(''),
  webhook: z
    .object({
      voiceUrl: z.string().optional().default(''),
      smsUrl: z.string().optional().default(''),
    })
    .default({}),
  metadata: z.record(z.string(), z.any()).default({}),
})
export type RelayablyWorkspacePhoneNumber = z.infer<typeof relayablyWorkspacePhoneNumberSchema>

export const relayablyPhoneComplianceNumberKindSchema = z.enum(['mobile', 'local', 'tollfree'])
export type RelayablyPhoneComplianceNumberKind = z.infer<typeof relayablyPhoneComplianceNumberKindSchema>

export const relayablyPhoneComplianceEndUserTypeSchema = z.enum(['individual', 'business'])
export type RelayablyPhoneComplianceEndUserType = z.infer<typeof relayablyPhoneComplianceEndUserTypeSchema>

export const relayablyPhoneComplianceBundleStatusSchema = z.enum([
  'not_started',
  'not_required',
  'draft',
  'pending_review',
  'in_review',
  'twilio_rejected',
  'twilio_approved',
  'provisionally_approved',
  'compliant',
  'noncompliant',
  'error',
])
export type RelayablyPhoneComplianceBundleStatus = z.infer<typeof relayablyPhoneComplianceBundleStatusSchema>

export const relayablyPhoneComplianceAddressSchema = z.object({
  friendlyName: z.string().default(''),
  customerName: z.string().default(''),
  street: z.string().default(''),
  streetSecondary: z.string().default(''),
  city: z.string().default(''),
  region: z.string().default(''),
  postalCode: z.string().default(''),
  isoCountry: z.string().default(''),
})
export type RelayablyPhoneComplianceAddress = z.infer<typeof relayablyPhoneComplianceAddressSchema>

export const relayablyPhoneComplianceEndUserSchema = z.object({
  firstName: z.string().default(''),
  lastName: z.string().default(''),
  email: z.string().default(''),
  phoneNumber: z.string().default(''),
  comments: z.string().default(''),
  businessName: z.string().default(''),
  businessRegistrationIdentifier: z.string().default(''),
  businessRegistrationNumber: z.string().default(''),
  businessWebsite: z.string().default(''),
  purposeForNumber: z.string().default(''),
  websiteUrl: z.string().default(''),
  documentNumber: z.string().default(''),
  businessIdentity: z.string().default(''),
  isSubassigned: z.boolean().default(false),
})
export type RelayablyPhoneComplianceEndUser = z.infer<typeof relayablyPhoneComplianceEndUserSchema>

export const relayablyPhoneComplianceDocumentSchema = z.object({
  id: z.string(),
  requirementName: z.string().default(''),
  label: z.string().default(''),
  documentType: z.string().default(''),
  fileName: z.string().default(''),
  mimeType: z.string().default(''),
  storagePath: z.string().default(''),
  twilioSupportingDocumentSid: z.string().default(''),
  status: z.string().default(''),
  createdAt: z.string().default(''),
  updatedAt: z.string().default(''),
})
export type RelayablyPhoneComplianceDocument = z.infer<typeof relayablyPhoneComplianceDocumentSchema>

export const relayablyPhoneComplianceProfileSchema = z.object({
  id: z.string(),
  country: z.string().default(''),
  numberKind: relayablyPhoneComplianceNumberKindSchema,
  endUserType: relayablyPhoneComplianceEndUserTypeSchema,
  twilioNumberType: z.string().default(''),
  regulationSid: z.string().default(''),
  regulationLabel: z.string().default(''),
  addressSid: z.string().default(''),
  endUserSid: z.string().default(''),
  bundleSid: z.string().default(''),
  evaluationSid: z.string().default(''),
  bundleStatus: relayablyPhoneComplianceBundleStatusSchema.default('not_started'),
  lastCheckedAt: z.string().default(''),
  submittedAt: z.string().default(''),
  approvedAt: z.string().default(''),
  notes: z.array(z.string()).default([]),
  errors: z.array(z.string()).default([]),
  address: relayablyPhoneComplianceAddressSchema.default({
    friendlyName: '',
    customerName: '',
    street: '',
    streetSecondary: '',
    city: '',
    region: '',
    postalCode: '',
    isoCountry: '',
  }),
  endUser: relayablyPhoneComplianceEndUserSchema.default({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    comments: '',
    businessName: '',
    businessRegistrationIdentifier: '',
    businessRegistrationNumber: '',
    businessWebsite: '',
    purposeForNumber: '',
    websiteUrl: '',
    documentNumber: '',
    businessIdentity: '',
    isSubassigned: false,
  }),
  documents: z.array(relayablyPhoneComplianceDocumentSchema).default([]),
})
export type RelayablyPhoneComplianceProfile = z.infer<typeof relayablyPhoneComplianceProfileSchema>

export const relayablyAssistantRoleSchema = z.enum([
  'personal_assistant',
  'business_receptionist',
  'lead_qualifier',
  'appointment_setter',
  'support_triage_assistant',
  'recruiter_screener',
])
export type RelayablyAssistantRole = z.infer<typeof relayablyAssistantRoleSchema>

export const relayablyVoiceSettingsSchema = z.object({
  speed: z.number().min(0.5).max(1.5).default(1),
  warmth: z.number().min(0).max(1).default(0.5),
  professionalism: z.number().min(0).max(1).default(0.8),
})
export type RelayablyVoiceSettings = z.infer<typeof relayablyVoiceSettingsSchema>

export const relayablyVoiceOptionSchema = z.object({
  id: z.string(),
  provider: z.enum(['relayably', 'elevenlabs', 'mock']).default('relayably'),
  name: z.string(),
  gender: z.string().default(''),
  age: z.string().optional().default(''),
  accent: z.string().optional().default(''),
  language: z.string().optional().default('en'),
  useCase: z.string().optional().default(''),
  category: z.string().optional().default(''),
  style: z.string().default(''),
  description: z.string().optional().default(''),
  previewLabel: z.string().default(''),
  previewScript: z.string().default(''),
  previewUrl: z.string().optional().default(''),
  supported: z.boolean().default(true),
  betaOnly: z.boolean().default(false),
})
export type RelayablyVoiceOption = z.infer<typeof relayablyVoiceOptionSchema>

export const relayablyKnowledgeBaseDocumentTypeSchema = z.enum(['text', 'url', 'file', 'folder'])
export type RelayablyKnowledgeBaseDocumentType = z.infer<typeof relayablyKnowledgeBaseDocumentTypeSchema>

export const relayablyKnowledgeBaseUsageModeSchema = z.enum(['auto', 'prompt'])
export type RelayablyKnowledgeBaseUsageMode = z.infer<typeof relayablyKnowledgeBaseUsageModeSchema>

export const relayablyKnowledgeBaseDocumentSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: relayablyKnowledgeBaseDocumentTypeSchema,
  usageMode: relayablyKnowledgeBaseUsageModeSchema.default('auto'),
  sourceKey: z.string().default(''),
  url: z.string().optional().default(''),
  lastSyncedAt: z.string().optional().default(''),
})
export type RelayablyKnowledgeBaseDocument = z.infer<typeof relayablyKnowledgeBaseDocumentSchema>

export const relayablyKnowledgeItemTypeSchema = z.enum(['text', 'url', 'file'])
export type RelayablyKnowledgeItemType = z.infer<typeof relayablyKnowledgeItemTypeSchema>

export const relayablyKnowledgeItemSourceSchema = z.enum(['manual', 'captured', 'system'])
export type RelayablyKnowledgeItemSource = z.infer<typeof relayablyKnowledgeItemSourceSchema>

export const relayablyKnowledgeItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: relayablyKnowledgeItemTypeSchema,
  content: z.string().optional().default(''),
  url: z.string().optional().default(''),
  fileName: z.string().optional().default(''),
  mimeType: z.string().optional().default(''),
  sizeBytes: z.number().optional().default(0),
  remoteDocumentId: z.string().optional().default(''),
  storagePath: z.string().optional().default(''),
  source: relayablyKnowledgeItemSourceSchema.default('manual'),
  createdAt: z.string().optional().default(''),
  updatedAt: z.string().optional().default(''),
})
export type RelayablyKnowledgeItem = z.infer<typeof relayablyKnowledgeItemSchema>

export const relayablyInstructionSourceSchema = z.enum(['text', 'voice', 'manual', 'captured'])
export type RelayablyInstructionSource = z.infer<typeof relayablyInstructionSourceSchema>

export const relayablyInstructionItemSchema = z.object({
  id: z.string(),
  content: z.string(),
  intentSummary: z.string().optional().default(''),
  source: relayablyInstructionSourceSchema.default('manual'),
  targetRoles: z.array(z.string()).default([]),
  createdAt: z.string().optional().default(''),
  updatedAt: z.string().optional().default(''),
})
export type RelayablyInstructionItem = z.infer<typeof relayablyInstructionItemSchema>

export const relayablyWorkspaceAssistantSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: relayablyAssistantRoleSchema,
  tone: z.string(),
  greeting: z.string(),
  fallbackBehaviour: z.string(),
  businessContext: z.string().optional().default(''),
  personalContext: z.string().optional().default(''),
  preferredEscalationMethod: z.string(),
  instructions: z.string().optional().default(''),
  phoneNumberId: z.string().optional().default(''),
  voiceId: z.string().optional().default(''),
  voiceSettings: relayablyVoiceSettingsSchema.default({
    speed: 1,
    warmth: 0.5,
    professionalism: 0.8,
  }),
})
export type RelayablyWorkspaceAssistant = z.infer<typeof relayablyWorkspaceAssistantSchema>

export const relayablyCalendarProviderSchema = z.enum(['calendly', 'cal.com'])
export type RelayablyCalendarProvider = z.infer<typeof relayablyCalendarProviderSchema>

export const relayablyWorkingDaySchema = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])
export type RelayablyWorkingDay = z.infer<typeof relayablyWorkingDaySchema>

export const relayablyWorkingHoursEntrySchema = z.object({
  day: relayablyWorkingDaySchema,
  closed: z.boolean().default(false),
  start: z.string().default('09:00'),
  end: z.string().default('17:00'),
})
export type RelayablyWorkingHoursEntry = z.infer<typeof relayablyWorkingHoursEntrySchema>

export const relayablyWorkingHoursSchema = z.object({
  days: z.array(relayablyWorkingDaySchema).default(['mon', 'tue', 'wed', 'thu', 'fri']),
  start: z.string().default('09:00'),
  end: z.string().default('17:00'),
  schedule: z
    .array(relayablyWorkingHoursEntrySchema)
    .default([
      { day: 'mon', closed: false, start: '09:00', end: '17:00' },
      { day: 'tue', closed: false, start: '09:00', end: '17:00' },
      { day: 'wed', closed: false, start: '09:00', end: '17:00' },
      { day: 'thu', closed: false, start: '09:00', end: '17:00' },
      { day: 'fri', closed: false, start: '09:00', end: '17:00' },
      { day: 'sat', closed: true, start: '09:00', end: '17:00' },
      { day: 'sun', closed: true, start: '09:00', end: '17:00' },
    ]),
})
export type RelayablyWorkingHours = z.infer<typeof relayablyWorkingHoursSchema>

export const relayablyCalendarIntegrationSchema = z.object({
  enabled: z.boolean().default(false),
  provider: relayablyCalendarProviderSchema.nullable().default(null),
  apiKey: z.string().default(''),
  timezone: z.string().default(''),
  workingHours: relayablyWorkingHoursSchema.default({
    days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    start: '09:00',
    end: '17:00',
  }),
})
export type RelayablyCalendarIntegration = z.infer<typeof relayablyCalendarIntegrationSchema>

export const relayablyWorkspaceConfigSchema = z.object({
  schemaVersion: z.string().default(relayablyWorkspaceSchemaVersion),
  workspaceName: z.string(),
  usageType: usageTypeSchema,
  businessName: z.string().optional().default(''),
  website: z.string().optional().default(''),
  businessDescription: z.string().optional().default(''),
  interestedCallNotes: z.string().optional().default(''),
  unsubscribeCallNotes: z.string().optional().default(''),
  industry: z.string(),
  preferredCommunicationStyle: communicationStyleSchema,
  primaryUseCase: primaryUseCaseSchema,
  useCaseFlags: useCaseFlagsSchema,
  phoneNumber: relayablyWorkspacePhoneNumberSchema.nullable().default(null),
  phoneComplianceProfiles: z.array(relayablyPhoneComplianceProfileSchema).default([]),
  voiceSelection: relayablyVoiceOptionSchema.nullable().default(null),
  assistant: relayablyWorkspaceAssistantSchema.nullable().default(null),
  knowledgeItems: z.array(relayablyKnowledgeItemSchema).default([]),
  instructions: z.array(relayablyInstructionItemSchema).default([]),
  knowledgeBaseFolderId: z.string().optional().default(''),
  knowledgeBase: z.array(relayablyKnowledgeBaseDocumentSchema).default([]),
  calendarIntegration: relayablyCalendarIntegrationSchema.default({
    enabled: false,
    provider: null,
    apiKey: '',
    timezone: '',
    workingHours: {
      days: ['mon', 'tue', 'wed', 'thu', 'fri'],
      start: '09:00',
      end: '17:00',
      schedule: [
        { day: 'mon', closed: false, start: '09:00', end: '17:00' },
        { day: 'tue', closed: false, start: '09:00', end: '17:00' },
        { day: 'wed', closed: false, start: '09:00', end: '17:00' },
        { day: 'thu', closed: false, start: '09:00', end: '17:00' },
        { day: 'fri', closed: false, start: '09:00', end: '17:00' },
        { day: 'sat', closed: true, start: '09:00', end: '17:00' },
        { day: 'sun', closed: true, start: '09:00', end: '17:00' },
      ],
    },
  }),
  templates: z.array(relayablyWorkspaceTemplateSchema).default([]),
  agentConfigs: z.array(relayablyWorkspaceAgentSchema).default([]),
})
export type RelayablyWorkspaceConfig = z.infer<typeof relayablyWorkspaceConfigSchema>

export const relayablyWorkspaceOnboardingSchema = z.object({
  mode: z.enum(['structured', 'conversational', 'hybrid']),
  source: z.enum(['structured', 'text', 'voice', 'hybrid']),
  currentStep: z.enum(['workspace-profile', 'dashboard']),
  completed: z.boolean(),
  summary: z.string().default(''),
  missingFields: z.array(z.string()).default([]),
})
export type RelayablyWorkspaceOnboarding = z.infer<typeof relayablyWorkspaceOnboardingSchema>

export const relayablyWorkspaceDocumentSchema = z.object({
  id: z.string().optional(),
  ownerUserId: z.string().optional(),
  profile: relayablyUserProfileSchema,
  workspace: relayablyWorkspaceConfigSchema,
  onboarding: relayablyWorkspaceOnboardingSchema,
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})
export type RelayablyWorkspaceDocument = z.infer<typeof relayablyWorkspaceDocumentSchema>

export const relayablyWorkspaceDraftSchema = z.object({
  profile: relayablyUserProfileSchema.partial().default({}),
  workspace: relayablyWorkspaceConfigSchema.partial().default({}),
  onboarding: relayablyWorkspaceOnboardingSchema.partial().default({}),
})
export type RelayablyWorkspaceDraft = z.infer<typeof relayablyWorkspaceDraftSchema>

export const relayablyAssistantIntentDraftSchema = z.object({
  assistant: relayablyWorkspaceAssistantSchema.partial().default({}),
  voice: z
    .object({
      voiceId: z.string().optional(),
      voiceSettings: relayablyVoiceSettingsSchema.partial().optional(),
    })
    .default({}),
})
export type RelayablyAssistantIntentDraft = z.infer<typeof relayablyAssistantIntentDraftSchema>

export const relayablyAssistantIntentResultSchema = z.object({
  summary: z.string(),
  confidence: z.number().min(0).max(1),
  missingFields: z.array(z.string()),
  draft: relayablyAssistantIntentDraftSchema,
  suggestedPreview: z.string().default(''),
})
export type RelayablyAssistantIntentResult = z.infer<typeof relayablyAssistantIntentResultSchema>

export const relayablyWorkspaceIntentResultSchema = z.object({
  summary: z.string(),
  confidence: z.number().min(0).max(1),
  missingFields: z.array(z.string()),
  draft: relayablyWorkspaceDraftSchema,
})
export type RelayablyWorkspaceIntentResult = z.infer<typeof relayablyWorkspaceIntentResultSchema>

export const defaultUseCaseFlags: UseCaseFlags = {
  screenPersonalCalls: false,
  captureSalesEnquiries: false,
  handleCustomerSupport: false,
  bookAppointments: false,
  qualifyLeads: false,
  protectFocusTime: false,
  afterHoursAnswering: false,
}

export const defaultCommunicationStyle: CommunicationStyle = {
  label: 'Warm and professional',
  tone: 'helpful, clear, and calm',
  formality: 'balanced',
  brevity: 'balanced',
  notes: '',
}

export const createDefaultWorkspaceDocument = (input: {
  email: string
  fullName: string
  country?: string
  timezone?: string
}): RelayablyWorkspaceDocument => ({
  profile: {
    fullName: input.fullName,
    email: input.email,
    country: input.country ?? '',
    timezone: input.timezone ?? 'UTC',
  },
  workspace: {
    schemaVersion: relayablyWorkspaceSchemaVersion,
    workspaceName: `${input.fullName} Workspace`,
    usageType: 'professional',
    businessName: '',
    website: '',
    businessDescription: '',
    interestedCallNotes: '',
    unsubscribeCallNotes: '',
    industry: '',
    preferredCommunicationStyle: defaultCommunicationStyle,
    primaryUseCase: 'protect_focus_time',
    useCaseFlags: {
      ...defaultUseCaseFlags,
      protectFocusTime: true,
    },
    phoneNumber: null,
    phoneComplianceProfiles: [],
    voiceSelection: null,
    assistant: null,
    knowledgeItems: [],
    instructions: [],
    knowledgeBaseFolderId: '',
    knowledgeBase: [],
    calendarIntegration: {
      enabled: false,
      provider: null,
      apiKey: '',
      timezone: '',
      workingHours: {
        days: ['mon', 'tue', 'wed', 'thu', 'fri'],
        start: '09:00',
        end: '17:00',
        schedule: [
          { day: 'mon', closed: false, start: '09:00', end: '17:00' },
          { day: 'tue', closed: false, start: '09:00', end: '17:00' },
          { day: 'wed', closed: false, start: '09:00', end: '17:00' },
          { day: 'thu', closed: false, start: '09:00', end: '17:00' },
          { day: 'fri', closed: false, start: '09:00', end: '17:00' },
          { day: 'sat', closed: true, start: '09:00', end: '17:00' },
          { day: 'sun', closed: true, start: '09:00', end: '17:00' },
        ],
      },
    },
    templates: [],
    agentConfigs: [],
  },
  onboarding: {
    mode: 'structured',
    source: 'structured',
    currentStep: 'workspace-profile',
    completed: false,
    summary: '',
    missingFields: ['country'],
  },
})
